import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { AddressSuggest } from '@/forms/components/AddressSuggest'
import { LookupCountrySelect } from '@/forms/components/LookupCountrySelect'
import { PickupPoint } from '@/interfaces/eshop'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'

const schema = z.object({
  name: z.string().min(1),
  street: z.string().min(1),
  city: z.string().min(1),
  zip: z.string().min(1),
  country: z.string().length(2),
  note: z.string(),
  openingHours: z.string(),
  isActive: z.boolean(),
})

type PickupPointFormValues = z.infer<typeof schema>

const defaultValues: PickupPointFormValues = {
  name: '',
  street: '',
  city: '',
  zip: '',
  country: 'CZ',
  note: '',
  openingHours: '',
  isActive: true,
}

interface PickupPointFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editingPoint: PickupPoint | null
  onSuccess: () => void
}

export function PickupPointForm({
  open,
  onOpenChange,
  editingPoint,
  onSuccess,
}: PickupPointFormProps) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop.pickupPoints',
  })
  const { t: te } = useTranslation(undefined, {
    keyPrefix: 'eshop',
  })

  const form = useForm<PickupPointFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  useEffect(() => {
    if (!open) return
    if (editingPoint) {
      form.reset({
        name: editingPoint.name,
        street: editingPoint.street,
        city: editingPoint.city,
        zip: editingPoint.zip,
        country: editingPoint.country,
        note: editingPoint.note ?? '',
        openingHours: editingPoint.openingHours ?? '',
        isActive: editingPoint.isActive,
      })
    } else {
      form.reset(defaultValues)
    }
  }, [open, editingPoint, form])

  const onSubmit = async (values: PickupPointFormValues) => {
    const payload = {
      ...values,
      note: values.note || null,
      openingHours: values.openingHours || null,
    }

    try {
      if (editingPoint) {
        await API.patch(`/api/v2/pickup-points/${editingPoint.id}`, payload)
        toast.success(t('edit.success'))
      } else {
        await API.post('/api/v2/pickup-points', payload)
        toast.success(t('create.success'))
      }
      onOpenChange(false)
      onSuccess()
    } catch (error: unknown) {
      toast.error(t(editingPoint ? 'edit.error' : 'create.error'), {
        description: extractErrorMessage(error),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingPoint ? t('edit.title') : t('create.title')}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('fields.namePlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="street"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.street')}</FormLabel>
                  <FormControl>
                    <AddressSuggest
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('fields.streetPlaceholder')}
                      onSelect={(address) => {
                        field.onChange(address.street)
                        form.setValue('city', address.city, {
                          shouldValidate: true,
                        })
                        form.setValue('zip', address.zip, {
                          shouldValidate: true,
                        })
                        form.setValue('country', address.country, {
                          shouldValidate: true,
                        })
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-3 gap-2">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.city')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zip"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.zip')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.country')}</FormLabel>
                    <FormControl>
                      <LookupCountrySelect
                        value={field.value}
                        onChange={field.onChange}
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="openingHours"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.openingHours')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('fields.openingHoursPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.note')}</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel>{t('fields.isActive')}</FormLabel>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {te('actions.cancel')}
              </Button>
              <Button type="submit">{te('actions.save')}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
